import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { convertEpochToDateString, GlobalFunctions } from '@global/GlobalFunctions';
import { AuthenticationService, IWebAppUser } from '@services/authentication.service';
import { IDataPoint } from '@services/datapoints-data.service';
import { IIntegratonSensor } from '@services/integration-sensors.service';
import { IntegrationsService, IWebhookIntegraton } from '@services/integrations.service';
import { ListView } from '@syncfusion/ej2-angular-lists';
import { combineLatest, firstValueFrom, Subject } from 'rxjs';
import { distinctUntilChanged, filter, finalize, map, take, takeUntil } from 'rxjs/operators';
import { SelectEventArgs, TabComponent } from '@syncfusion/ej2-angular-navigations';
import { NeuronsensorsService, nsWebhookConfig } from '@services/neuronsensors.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { ComponentType } from '@angular/cdk/portal';
import { MatDialog } from '@angular/material/dialog';
import { IOrganisation, Organisation } from '@services/organisation-data.service';
import { json } from 'express';
import { Account, IAccount } from '@services/accounts.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { EditWebhookIntegrationComponent } from 'src/app/dialogs/edit-webhook-integration/edit-webhook-integration.component';
import { EditWebhookIntegrationNeuronSensorComponent } from 'src/app/dialogs/edit-webhook-integration-neuronsensor/edit-webhook-integration-neuronsensor.component';

@Component({
	selector: 'app-data-integrations-detail',
	templateUrl: './data-integrations-detail.component.html',
	styleUrls: ['./data-integrations-detail.component.scss']
})
export class DataIntegrationsDetailComponent implements OnInit, OnDestroy {

	@ViewChild('outlet') outlet: RouterOutlet;
	@ViewChild('pageTabs') public pageTabs: TabComponent;

	/** Route Organisation ID */
	public routeOrganisationID: string

	/** Selected Account GUID */
	public selectedAccountGUID: string

	/** Selected Account */
	public selectedAccount: IAccount

	/** selected Tab Index */
	public selectedTabIndex: number;

	/** Tab Mpping */
	private tabMap = ['details', 'sensors', 'neuroninfo'];

	/** Supress Tab Change */
	private suppress = false;

	/** Route Webhook ID */
	public routeWebhookID: string

	/** Webhook Integration */
	public webhookIntegration: IWebhookIntegraton

	/** Current User */
	public currentUser: IWebAppUser

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Array of Sensors */
	public sensorsList: IIntegratonSensor[]

	/** filtered Sensors List */
	public filteredSensorsList: IIntegratonSensor[]

	/** Selected Sensor */
	public selectedSensor: IIntegratonSensor;

	/** Sensors Datapoints */
	public sensorDatapoints: IDataPoint[]

	private MyOrganisations: IOrganisation[];

	private myAccounts: IAccount[]

	@ViewChild('listviewInstance') public listviewInstance: ListView;
	public dataSource: object;
	public cssClass: string = 'e-list-template';
	isMobile: boolean;
	count: number = 0;

	public isNeuronSensor: boolean = false

	/**
	 * Default Constructor
	 * @param globalFunctions Global Functions
	 * @param authenticationService Authentication Services
	 * @param activatedRoute ActivatedRoute
	 * @param siteSubscriptions siteSubscriptions
	 * @param integrationService integrationService
	 * @param neuronsensorsService neuronsensorsService
	 * @param router Router
	 * @param dialog MatDialog
	 * @param clipboard clipboard
	 * @param siteSubscriptions siteSubscriptions
	 */
	constructor(
		private globalFunctions: GlobalFunctions,
		public authenticationService: AuthenticationService,
		private activatedRoute: ActivatedRoute,
		private integrationService: IntegrationsService,
		private neuronsensorsService: NeuronsensorsService,
		private router: Router,
		private dialog: MatDialog,
		private clipboard: Clipboard,
		private siteSubscriptions: siteSubscriptions
	) { }

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		combineLatest([
			this.authenticationService.currentUser$.pipe(take(1)),
			this.siteSubscriptions.selectedAccount$.pipe(take(1)),
			this.siteSubscriptions.myAccounts$.pipe(take(1)),
			this.siteSubscriptions.myOrganisations$.pipe(take(1)),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(take(1)),
			this.activatedRoute.paramMap.pipe(
				map(p => p.get('integrationID')),
				distinctUntilChanged(),
				takeUntil(this.finalise)
			),
			this.activatedRoute.queryParamMap.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([user, account, myAccounts, myOrganisations, selectedOrganisationUID, integrationID, queryParamMap]) =>
				!!user && !!account &&
				Array.isArray(myAccounts) && myAccounts.length > 0 &&
				Array.isArray(myOrganisations) && myOrganisations.length > 0 &&
				!!selectedOrganisationUID && !!integrationID)
		).subscribe({
			next: ([user, account, myAccounts, myOrganisations, selectedOrganisationUID, integrationID, queryParamMap]) => {
				this.currentUser = <IWebAppUser>user
				this.routeWebhookID = integrationID ?? "";

				this.selectedAccount = account;
				this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;
				this.selectedAccountGUID = this.selectedAccount.GUID;

				this.routeOrganisationID = selectedOrganisationUID

				this.MyOrganisations = myOrganisations;
				this.myAccounts = myAccounts;

				if (queryParamMap && queryParamMap.has('isneuron')) {
					this.isNeuronSensor = (queryParamMap.get('isneuron') == "true") ? true : false;
				}
				if (queryParamMap && queryParamMap.has('tab')) {
					const idx = this.tabMap.indexOf(queryParamMap.get('tab') ?? '');
					this.suppress = true;
					this.selectedTabIndex = Math.max(idx, 0);
					queueMicrotask(() => this.suppress = false);
				}

				this.getIntegrations(this.selectedAccountGUID, this.routeOrganisationID, this.routeWebhookID, 0)
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → Error: Error in one of the combined subscriptions`, false);
			}
		});
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * onChange
	 * @param args 
	 * @returns 
	 */
	onChange(args: SelectEventArgs) {
		if (this.suppress) return;
		const index = args.selectedIndex;
		// const tab = this.accountTabs.tabId
		const tab = this.tabMap[index];
		this.router.navigate([],
			{
				relativeTo: this.activatedRoute,
				queryParams: { tab },
				queryParamsHandling: 'merge', // keep existing params
				replaceUrl: true              // optional: avoids polluting history
			}
		);
	}

	/** Array of Webhooks */
	public webhookIntegrations: IWebhookIntegraton[]
	/** Selected Integration */
	public selectedIntegration?: IWebhookIntegraton
	private validationCache = new Map<string, nsWebhookConfig>();
	
	/**
	 * Get the Integrations for the Account Organisation filtered by Type
	 * @param accountGUID
	 * @param organisationUID
	 * @param WebhookUID 
	 * @param typeID
	 */
	async getIntegrations(accountGUID: string = "", organisationUID: string = "", webhookUID: string = "", typeID: number = 0) {

		const queryParams: Record<string, any> = {};

		try {
			const integrations = await firstValueFrom(
				this.integrationService.getWebhookIntegrations$(accountGUID, organisationUID, webhookUID, queryParams)
					.pipe(
						filter((integrations) => !!integrations),
						distinctUntilChanged()
					)
			);
			this.webhookIntegrations = <IWebhookIntegraton[]>integrations
			if (Array.isArray(this.webhookIntegrations)) {
				this.selectedIntegration = this.webhookIntegrations.find((x: IWebhookIntegraton) => x.WebhookUID == webhookUID);
			} else {
				this.selectedIntegration = this.webhookIntegrations;
			}

			if (this.selectedIntegration != null) {
				const varOrg = this.MyOrganisations.find(org => org.Uid == this.selectedIntegration?.OrganisationUID)
				const varAcc = this.myAccounts.find(acc => acc.GUID == this.selectedIntegration?.AccountUID)
				this.selectedIntegration.OrganisationName = varOrg?.Name;
				this.selectedIntegration.AccountName = varAcc?.Name;
			}

			// 🔹 Validate each integration asynchronously (non-blocking)
			// if (this.selectedIntegration?.NeuronSensors) {
			// 	var rtn = await this.getNSWebhooks(this.selectedIntegration); // no await -> fire and forget
			// 	if (rtn) {
			// 		this.validationCache.set(rtn.id, rtn);
			// 	}
			// }


		} catch (error) {
			this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → this.integrationService.getWebhookIntegrations$`, false)
		}
	};

	/**
	 * getNSWebhooks
	 * @param integration 
	 * @returns 
	 */
	async getNSWebhooks(integration: IWebhookIntegraton): Promise<nsWebhookConfig | undefined> {

		var apiKey = integration.NeuronSensorAPI
		var systemID = integration.NeuronSensorSystem
		var webhookID = integration.ClientWebhookID

		try {
			const integration = await firstValueFrom(this.neuronsensorsService
				.getIntegrationByID$(apiKey, systemID, webhookID)
				.pipe(
					filter((integrations) => !!integrations)
				)
			);

			return integration		

		} catch (error) {
			console.error("Error fetching webhooks:", error);
			return undefined;
		}

	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	};

	ValidateNeuron(integration: IWebhookIntegraton): string {

		if (integration.NeuronSensors) {
			if (this.validationCache.has(integration.ClientWebhookID)) {
				var fred = this.validationCache.get(integration.ClientWebhookID);
				if (fred) {
					if (fred.disabled >= 100)
						return "Disabled"
					else if (fred.disabled >= 50 && fred.disabled < 100)
						return "Warning"
					else
						return "OK"
				} else {
					return ""
				}
			}
		}
		return "";
	}

	/**
	 * copy To Clipboard
	 * @param integration
	 */
	copyToClipboard(integration: IWebhookIntegraton) {
		try {
			let text = integration.FQN;
			if (this.clipboard.copy(text)) {
				this.globalFunctions.DisplaySnackBar({
					message: "Path copied to clipboard",
					action: "",
					duration: 2000
				});
			};
		} catch (error) { console.error(error); }
	};

	/**
	 * editWebhook
	 * @param selectedIntegration 
	 */
	/**
		 * editIntegration
		 */
	async editIntegration(integration: IWebhookIntegraton) {

		try {
			// Create a 'Local'/shallow copy of the integration Information
			var _edit = { ...integration }

			// Choose the dialog component
			const component: ComponentType<any> = _edit.NeuronSensors
				? EditWebhookIntegrationNeuronSensorComponent
				: EditWebhookIntegrationComponent;

			// Display the dialog box
			const dialogRef = this.dialog.open(component, { data: _edit });

			// Update the original Asset with the result
			const result = await firstValueFrom(
				dialogRef.afterClosed().pipe()
			);
			if (!result) { return; }

			// Check we have a result to work with
			this.globalFunctions.displaySpinner(true)
			Object.assign(_edit, result);
			const params = {
				"selectedAccountGUID": "this.selectedAccountGUID",
				"Uid": "this.selectedOrganisation.Uid",
				"WebhookID": _edit.WebhookUID,
				"integrationID": _edit.OrganisationWebhookID
			}
			this.integrationService.patchWebhookIntegration$(this.selectedAccountGUID, this.routeOrganisationID, _edit.OrganisationWebhookID, _edit,).pipe(
				take(1), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => { this.globalFunctions.displaySpinner(false); })
			).subscribe({
				next: (integrations) => {
					let data = <IWebhookIntegraton>integrations

					if (data.NeuronSensors) {
						var nsIntegration = {} as nsWebhookConfig
						nsIntegration.id = data.ClientWebhookID
						nsIntegration.type = "POST"
						// If deleted or Not Enabled, then automatically disable the Neuronsenor Webhook
						if (data.Deleted || !data.Enabled) {
							nsIntegration.disabled = 150
							this.patchWebhook(data.NeuronSensorAPI, data.NeuronSensorSystem, nsIntegration)
						} else {
							/** If Re-Enabled  */
							if (data.Enabled && !integration.Enabled) {
								this.globalFunctions.UserConfirm$(`Enable this webhook on NeuronSensors?`)
									.subscribe(confirmed => {
										if (confirmed) {
											nsIntegration.disabled = 0
											this.patchWebhook(data.NeuronSensorAPI, data.NeuronSensorSystem, nsIntegration)
										}
									});
							}
						}
					}
					Object.assign(integration, data);
					this.globalFunctions.DisplaySnackBar({ message: `Webhook ${data.Description} had been Updated`, action: "" })

				},
				error: (error) => { this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → Patching Integrations`, false) },
			});

		}
		catch (error) {
			//this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → Patching Integrations`, false)
		}

	};

	/**
	 * patchWebhook
	 * @param _edit 
	 */
	patchWebhook(apiKey: string, systemID: string, _edit: nsWebhookConfig) {
		this.neuronsensorsService.patchIntegration$(apiKey, systemID, _edit).pipe(
			take(1), distinctUntilChanged(),
			filter((success) => !!success)
		).subscribe({
			next: (success) => { },
			error: (error) => { this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → Error: Error patching Webhook`, true); }
		})
	}
}
